<template>
	<div id="app">
		<AppHeader v-if="!isIframed" />

		<progress-bar
			v-if="showProgress"
			:progress="progress"
		/>

		<div
			id="content-container"
			class="container"
		>
			<div
				v-if="step === 1"
				class="row free-quote"
			>
				<h3
					class="col-12"
				>
					Get Free Moving Quotes PLUS a Free Estimate
				</h3>
				<h5
					class="col-12"
				>
					Enter ZIP From, ZIP To — View Prices
				</h5>
			</div>

			<div
				:class="[`${currentStepName}-page`]"
				class="dynamic-content"
			>
				<div
					:style="quoteStyle"
					class="page-content"
				>
					<div
						v-if="step > 1 && step < 5"
						class="quote-steps"
					>
						<progress-steps
							:step="step"
							:on-change="onStepChange"
						/>
					</div>
					<router-view></router-view>
				</div>
			</div>
			<MovingScene />
		</div>

		<ClientLogos v-if="!isIframed" />

		<AppFooter v-if="!isIframed" />
	</div>
</template>

<script>
import { mapState } from 'vuex';

import AppHeader from '@/components/AppHeader.vue';
import MovingScene from '@/common/MovingScene.vue';
import progressbar from '@/common/progressbar';

const AppFooter = () => import(/* webpackChunkName: "main-App" */'@/components/AppFooter.vue');
const ClientLogos = () => import(/* webpackChunkName: "main-App" */'@/common/ClientLogos.vue');
const steps = () => import(/* webpackChunkName: "main-App" */'@/common/steps');

export default {
	name: 'App',
	components: {
		AppHeader,
		AppFooter,
		ClientLogos,
		MovingScene,
		'progress-bar': progressbar,
		'progress-steps': steps,
	},
	data: function() {
		return {
			progress: 0,
			showProgress: false,
			step: 1,
		};
	},
	computed: {
		...mapState({
			isIframed: state => state.iframeFlag,
			quote: state => state.quote,
			completed_steps: state => state.progress.completed_steps,
		}),

		currentStepName() {
			let currentStep = this.$store.getters['progress/getStepByIndex'](this.step - 1);

			if (!currentStep) {
				currentStep = {
					name: this.$route.name,
				};
			}

			return currentStep.name.toLowerCase();
		},

		quoteStyle() {
			const url = this.$store.state.progress.quote_bg;

			if (url && this.step >= 5 && this.step < 8) {
				return {
					'background-image': `url(${url})`,
					'background-position': '50% 0%',
					'background-repeat': 'no-repeat',
					'background-size': 'auto 100%',
					'max-width': '100%',
				};
			}

			return null;
		},
	},
	watch: {
		$route(to, from) {
			this.$log.debug(to, from);

			if (to.meta.hasOwnProperty('analytics')) {
				this.$log.info('analytics.page', to.meta.analytics);

				this.$analytics.page(to.meta.analytics.pageName);
			}
		},
		completed_steps(to) {
			this.step = to;

			const step = this.step;

			if (step > 0 && step < 5) {
				this.showProgress = true;

				this.progress = this.$store.getters['progress/getProgress'](step);
			} else {
				this.showProgress = false;
			}

			this.$log.debug('current step', step);
		},
	},
	mounted: function() {
		this.$log.info('VERSION: ' + require('../package.json').version);
	},
	methods: {
		onStepChange: function(step) {
			const route = this.$store.getters['progress/getStepByIndex'](step);

			this.$log.debug(route);

			if (route) {
				this.$router.push(route);
			}
		},
	},
};
</script>

<style lang="scss">
@import "~@/../node_modules/bootstrap/scss/bootstrap-reboot";
@import "~@/assets/scss/animations";

html body {
	font-size: 67.5% !important;
	opacity: 1 !important;
}

#app {
	background-color: #d0e8f4;
	color: #2c3e50;
	height: 100%;
	text-align: center;

	&.loading {
		height: 100%;
		max-height: 100%;
		overflow: hidden;
		position: fixed;
		width: 100%;
	}
}

input:focus,
button:focus {
	box-shadow: none !important;
	outline: none !important;
}

#content-container {
	height: 80vh;
	max-height: 720px;
	padding: 4vh 20px;
	position: relative;
}

.free-quote {
	h3 {
		color: #df5435;
		font-weight: bold;
		margin: auto;
		margin-bottom: 5px;
	}

	h5 {
		margin: auto;
	}
}

.white-bg {
	background: white !important;
}

.slide-fade-enter-active {
	transition: all 0.3s ease-in;
}
.slide-fade-leave-active {
	transition: all 0.3s ease-out;
}
.slide-fade-leave-to {
	opacity: 0;
	transform: translateX(-300px);
}
.slide-fade-enter {
	opacity: 0;
}

.dynamic-content {
	font-size: 90%;
	height: 100%;
	margin: auto;
	padding: 3vh 0;
	position: relative;
	z-index: 99;

	.page-content {
		height: 100%;
		margin: 0 auto;
		max-width: 550px;
	}

	&[class^="quote-"] {
		.page-content {
			max-width: 100%;
		}
	}
}

.page-content {
	height: auto;

	.quote-steps {
		float: right;
		margin-left: 5%;
		width: 5%;
	}
}

.primary-hd {
	color: #4a4a4a;
	font-size: 1.7rem;
	margin: 10px 0;
}

.sec-hd {
	color: #3bb671;
	font-size: 1.1rem;
	font-weight: 400;
	margin: 0;
	min-height: 15px;
}

input.form-control {
	background-color: rgba(100, 181, 217, 0.32);
	border: 0;
	border-bottom: 1.5px solid #a7a7a7;
	border-radius: 4px 4px 0 0;
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
	font-size: 1.3rem !important;
	font-weight: 700;
	height: 50px;
}

button {
	background: #df5435;
	border: 0;
	border-radius: 40px !important;
	color: white;
	cursor: pointer;
	font-size: 24px !important;
	font-weight: bold !important;
	height: 45px;
	padding: 0 1rem !important;
	user-select: none;

	&.btn-primary,
	&.btn-primary:hover,
	&.btn-primary:active {
		background: #3bb671;
		border: none;
	}

	&:focus {
		box-shadow: none !important;
	}
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
	appearance: none;
	margin: 0;
}

input[type="number"] {
	appearance: textfield;
}

input::-ms-clear {
	display: none;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
	transition: background-color 5000s ease-in-out 0s;
}

.auto-form,
.inline-form {
	font-size: 1.5rem;
	margin-bottom: 0;

	input.form-control {
		background-color: transparent;
		border: 0;
		max-height: 50px; // Fix for IE browsers
		width: 88%;

		&:focus {
			background-color: transparent;
			outline: none;
		}

		&::placeholder {
			color: #a7a7a7 !important;
		}
	}

	button {
		cursor: pointer;
		min-height: 50px;
		min-width: 170px;
	}
}

@include media-breakpoint-down(sm) {
	.free-quote {
		h3 {
			font-size: 1.3rem;
			padding: 15px 45px;
		}
		h5 {
			font-size: 1.1rem;
			padding: 10px 30px;
		}
	}

	.primary-hd {
		font-size: 1.4rem;
		text-align: center;
	}

	.page-content {
		align-items: center;
		display: flex;
		flex-direction: column;

		.quote-steps {
			width: 100%;

			.progress-steps {
				display: flex;
				justify-content: center;

				p {
					display: none;
				}

				li {
					height: 15px;
					width: 15px;
				}
			}
		}

		.quote-form {
			align-items: center;
			display: flex;
			flex-direction: column;

			.sec-hd {
				text-align: center;
			}
		}
	}
}
</style>
